import { useEffect, useRef, useState, ChangeEvent } from "react";
// @ts-ignore
import SignatureCanvas from "react-signature-canvas";

import plus from "../../images/plus.svg";
import check from "../../images/check.svg";

type ButtonSignatureProps = {
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
  isLabelOptionnal?: boolean;
};

type SignatureMode = "drawn" | "typed";

const SIGNATURE_WIDTH = 300;
const SIGNATURE_HEIGHT = 200;

const renderTypedSignature = (name: string): string => {
  const canvas = document.createElement("canvas");
  canvas.width = SIGNATURE_WIDTH;
  canvas.height = SIGNATURE_HEIGHT;
  const ctx = canvas.getContext("2d");
  if (!ctx) return "";

  ctx.fillStyle = "#fff";
  ctx.fillRect(0, 0, SIGNATURE_WIDTH, SIGNATURE_HEIGHT);

  ctx.fillStyle = "#000";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";

  // Auto-fit font size to canvas width
  let fontSize = 44;
  ctx.font = `400 ${fontSize}px "Dancing Script", "Brush Script MT", cursive`;
  while (ctx.measureText(name).width > SIGNATURE_WIDTH - 30 && fontSize > 16) {
    fontSize -= 2;
    ctx.font = `400 ${fontSize}px "Dancing Script", "Brush Script MT", cursive`;
  }

  ctx.fillText(name, SIGNATURE_WIDTH / 2, SIGNATURE_HEIGHT / 2);
  return canvas.toDataURL("image/png");
};

function ButtonSignature({
  required,
  onChange,
  value,
  isLabelOptionnal = true,
}: ButtonSignatureProps) {
  const signatureCanvasRef = useRef<any>();

  const [isModal, setIsModal] = useState(false);
  const [currentSignature, setCurrentSignature] = useState<string>(value);
  const [mode, setMode] = useState<SignatureMode>("drawn");
  const [typedName, setTypedName] = useState("");

  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    onChange(currentSignature);
  }, [currentSignature]);

  const switchMode = (next: SignatureMode) => {
    if (next === mode) return;
    setMode(next);
    window.umami?.track("signature_mode_chosen", { mode: next });
  };

  const handleValidate = () => {
    if (mode === "drawn") {
      if (signatureCanvasRef.current && !signatureCanvasRef.current.isEmpty()) {
        const dataUrl = signatureCanvasRef.current.toDataURL();
        setCurrentSignature(dataUrl);
        window.umami?.track("signature_validated", {
          mode: "drawn",
        });
      }
    } else {
      const trimmed = typedName.trim();
      if (trimmed.length > 0) {
        const dataUrl = renderTypedSignature(trimmed);
        setCurrentSignature(dataUrl);
        window.umami?.track("signature_validated", {
          mode: "typed",
          name_length: trimmed.length,
        });
      }
    }
    setIsModal(false);
  };

  const handleClear = () => {
    if (mode === "drawn") {
      signatureCanvasRef.current?.clear();
    } else {
      setTypedName("");
    }
    setCurrentSignature("");
  };

  const handleCancel = () => {
    window.umami?.track("signature_modal_cancelled", {
      mode,
      had_drawn_content:
        mode === "drawn" && signatureCanvasRef.current
          ? !signatureCanvasRef.current.isEmpty()
          : false,
      had_typed_content: mode === "typed" ? typedName.trim().length > 0 : false,
    });
    if (mode === "drawn") {
      signatureCanvasRef.current?.clear();
    } else {
      setTypedName("");
    }
    setIsModal(false);
  };

  return (
    <>
      <div className="button-picture-container">
        {currentSignature ? (
          <>
            <div className="button-picture-viewer">
              <img src={currentSignature} alt="signature" />
            </div>
          </>
        ) : null}
        <button
          className="button-signature"
          onClick={() => {
            window.umami?.track("signature_modal_opened");
            setIsModal(true);
          }}
        >
          <span>
            Signature{" "}
            {required
              ? "(Obligatoire)"
              : isLabelOptionnal
              ? "(Facultatif)"
              : ""}
          </span>
          <img alt="plus" src={currentSignature ? check : plus} />
        </button>
      </div>
      {isModal ? (
        <div
          className="button-signature-modal-container"
          onClick={(e) => {
            if (e.target === e.currentTarget) handleCancel();
          }}
        >
          <div className="button-signature-modal">
            <button
              type="button"
              className="signature-cancel-icon"
              onClick={handleCancel}
              aria-label="Fermer"
            >
              ×
            </button>
            <div className="signature-mode-tabs">
              <button
                type="button"
                className={`signature-mode-tab${
                  mode === "drawn" ? " active" : ""
                }`}
                onClick={() => switchMode("drawn")}
              >
                Signer au doigt
              </button>
              <button
                type="button"
                className={`signature-mode-tab${
                  mode === "typed" ? " active" : ""
                }`}
                onClick={() => switchMode("typed")}
              >
                Saisir mon nom
              </button>
            </div>

            {mode === "drawn" ? (
              <SignatureCanvas
                ref={(ref: any) => {
                  signatureCanvasRef.current = ref;
                  if (ref) {
                    const pad = ref.getSignaturePad();
                    if (!pad._strokeUpdatePatched) {
                      const originalStrokeUpdate = pad._strokeUpdate.bind(pad);
                      pad._strokeUpdate = function (event: any) {
                        if (this._data && this._data.length > 0) {
                          originalStrokeUpdate(event);
                        }
                      };
                      pad._strokeUpdatePatched = true;
                    }
                  }
                }}
                penColor="black"
                canvasProps={{
                  width: SIGNATURE_WIDTH,
                  height: SIGNATURE_HEIGHT,
                  className: "sigCanvas",
                }}
                backgroundColor="#fff"
              />
            ) : (
              <div className="signature-typed-container">
                <input
                  type="text"
                  className="signature-typed-input"
                  placeholder="Nom Prénom"
                  value={typedName}
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    setTypedName(e.target.value)
                  }
                  autoFocus
                />
                <div
                  className="signature-typed-preview"
                  style={{ width: SIGNATURE_WIDTH, height: SIGNATURE_HEIGHT }}
                >
                  {typedName.trim() ? (
                    <span className="signature-typed-text">{typedName}</span>
                  ) : (
                    <span className="signature-typed-placeholder">
                      Aperçu de votre signature
                    </span>
                  )}
                </div>
              </div>
            )}

            <div>
              <button className="button-signature outline" onClick={handleClear}>
                Effacer
              </button>
              <button className="button-signature" onClick={handleValidate}>
                Valider
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}

export default ButtonSignature;
